"use client";

import { cn } from "@/lib/utils";
import useTranslate from "@app/_hooks/useTranslate";
import useAddToCart from "@features/cart/hooks/useAddToCart";
import { Button } from "@ui/button";
import { CheckIcon } from "lucide-react";
import { ComponentProps, type FormEvent, useEffect, useState } from "react";

import type { CartProduct } from "../utils/toCartProduct";

type ProductSampleButtonProps = {
  product: CartProduct;
  inStock: boolean;
  market: string;
} & ComponentProps<"form">;

export default function ProductSampleButton({
  product,
  inStock,
  market,
  className,
  ...props
}: ProductSampleButtonProps) {
  const t = useTranslate();
  const { mutateAsync: addToCart, isPending, isError } = useAddToCart();
  const [added, setAdded] = useState(false);

  useEffect(() => {
    if (!added) return;
    const timeout = window.setTimeout(() => setAdded(false), 3000);
    return () => window.clearTimeout(timeout);
  }, [added]);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!inStock) return;

    try {
      await addToCart([product]);
      setAdded(true);
    } catch (error) {
      console.error(error);
    }
  }

  return (
    <form
      onSubmit={handleSubmit}
      className={cn("col-span-full", className)}
      {...props}
    >
      <input type="hidden" name="productKey" value={product.key} />
      <input type="hidden" name="market" value={market} />
      <input type="hidden" name="quantity" value="1" />
      <Button
        type="submit"
        disabled={!inStock}
        variant="outline"
        className={cn(
          "w-full",
          (added || isPending) &&
            "pointer-events-none border-0 bg-green-600 text-white",
        )}
      >
        {added ? (
          <CheckIcon strokeWidth={1} className="size-5" />
        ) : (
          <span>
            {t("purchaseSample")}
            {!inStock ? ` (${t("outOfStock")})` : null}
          </span>
        )}
      </Button>
      {isError ? (
        <p className="text-error mt-1 text-sm">{t("errorAddToCart")}</p>
      ) : null}
    </form>
  );
}
